import type { SavedView } from './views';
import type { PortfolioSavedView } from './portfolio-views';
import type { ProjectRole } from './permissions';

/**
 * A person on this board.
 *
 * The email is the identity — it is what Firebase signs in and what the rules
 * check — but it is not a name, and it should not be what other people read on
 * a comment. The profile hangs off the email so the two can never disagree:
 * there is no separate list of names to keep in step, which is what went wrong
 * before (a free-text member list beside an unrelated list of addresses).
 */
export interface Profile {
  name?: string;
  title?: string; // what they do here, e.g. "Designer" or "Advising on the API"
  // THE STANDING RACI ROLE across the workspace (two-part assignee, Feature A) —
  // distinct from the free-text `title` above. Drives which slot (Accountable vs
  // Responsible) this person's self-assignment fills on a new card. Admin-set in
  // Workspace › People. Absent = not set (the app falls back to Accountable). See
  // src/company-role.ts for the values and the default mapping.
  companyRole?: import('./company-role').CompanyRole | null;
  // A per-person override of how their self-assignment fills a new card: follow
  // the company role (the default), Always Accountable, Always Responsible, or
  // Don't auto-assign. Absent/null reads as 'follow'. Admin-set, same panel.
  cardDefaultStance?: import('./company-role').CardStance | null;
  notifyWhen?: 'all' | 'mentions' | 'none'; // yours to set; nobody else's business
  /**
   * When this person answered the questions that are about THEM.
   *
   * Held apart from the project's own marker because the two are asked of
   * different people. Somebody joining a board that is already set up should
   * still be asked their name — and should never be walked through settings that
   * were decided before they arrived and are not theirs to change.
   */
  setUpAt?: number;
  /**
   * Which version of the terms this person accepted, and when.
   *
   * Kept per person and per copy — the profiles collection is prefixed, so
   * accepting on the test copy is not accepting on the real board. That is
   * correct rather than incidental: the two agreements differ, because only one
   * of them says "this is a test environment and not everything works".
   *
   * The version matters more than the timestamp. "They agreed" is worth nothing
   * without saying to WHAT, and the wording changes.
   */
  eulaVersion?: number;
  eulaAcceptedAt?: number;
  /**
   * Where this person wants to be emailed, when there is anything to email.
   *
   * Separate from the address they sign in with, and deliberately so: the
   * sign-in address is an identity and cannot be changed without changing who
   * you are, while where you read your post is a preference. Plenty of people
   * sign in with a work address and want notice somewhere they actually look.
   *
   * WHERE THE AWAY-EMAIL GOES. The scheduled call-out digest (#413) sends to
   * this address when it is set, and to the sign-in address when it is not — so
   * nobody is emailed at an address they never chose. Whether the digest sends
   * at all is `emailCallouts` (and `notifyWhen`).
   */
  /**
   * Given name and family name, held apart.
   *
   * `name` remains the joined form, so everything that shows a person reads
   * one field and nothing had to learn about halves — and a profile written
   * before this existed still works with only `name`.
   */
  firstName?: string;
  lastName?: string;
  notifyEmail?: string;
  /** Whether the away-email digest sends to this person at all — #413. Absent
   *  means yes (the column defaults true); off silences email without touching
   *  the in-app bell. */
  emailCallouts?: boolean;
  /**
   * When this person last chose a password of their own.
   *
   * Absent means never, and never means they are asked to choose one before the
   * board — an admin makes the account and the person makes the password, so
   * nobody but them has ever known it. There is no temporary password to leak,
   * to forget to change, or to be shared over a chat message.
   */
  passwordSetAt?: number;
  /**
   * WHEN THIS PERSON WAS DEACTIVATED (suspended) — `people.suspended_at`, 0208.
   *
   * Absent/null means active. A moment rather than a flag so the trail can read
   * "deactivated on …", and so reactivating is a single write of null that keeps
   * every role and board exactly as they stood — deactivation is reversible, not
   * a removal. The app gates sign-in on it in the same place it gates
   * `passwordSetAt` (App.tsx): a suspended person reaches a "your access is
   * paused" screen and no further, while everything about their access is kept so
   * turning it back on restores them whole. Admin-set, like the company role.
   */
  suspendedAt?: number | null;
  /**
   * This is an assistant, not a colleague — `people.is_assistant`, since 0025.
   *
   * WHY THE APP NEEDS TO KNOW, having managed without it. It gates the board on
   * `passwordSetAt`, and the reasoning behind that gate is entirely about a
   * PERSON: somebody invited arrives holding a link out of their inbox, and
   * until they have chosen a password that link is all there is between an old
   * forwarded email and their account.
   *
   * None of that is true of an assistant. It has no inbox, there was never a
   * link, and it never chooses anything — the password it was handed IS its
   * password. So the gate has nothing to protect and everything to break: an
   * account whose password is right, sent to choose a password, by a screen no
   * script can answer. #304.
   */
  assistant?: boolean;
  /**
   * The backlog views this person saved — #301.
   *
   * ON THE PERSON so they follow them to another machine, which is what Vinnie
   * asked for on 30 August 2026: *"per person, but follows them"*. Ordinary
   * filters stay in localStorage, because they are a transient state of one
   * screen; a view somebody NAMED is not, and finding it only on the laptop you
   * built it on quietly teaches people not to use the feature.
   */
  savedViews?: SavedView[];
  /**
   * The Portfolio roll-up views this person saved, and the id of the one they
   * open on — the same "per person, follows them" store as savedViews, for the
   * Portfolio's own toolbar (a preset lens + dimension filters + grouping +
   * sort). `portfolioDefault` may name a built-in lens or one of these; absent
   * means the app falls back to a built-in. See migration 0121.
   */
  portfolioViews?: PortfolioSavedView[];
  portfolioDefault?: string;
  /**
   * The moment "sign out everywhere" was pressed.
   *
   * A moment rather than a list of what to end, so it cannot be raced: every
   * browser that signed in before it takes itself off, and anything after is
   * by definition a fresh sign-in. See src/sessions.ts.
   */
  signOutAfter?: number;
}

export interface Access {
  emails: string[];
  /** Workspace admins. Held apart from project roles so a manager cannot reach it. */
  admins: string[];
  /**
   * EXECUTIVE PAYMENT APPROVERS — the people who may be named as a signer on a
   * payment-approval chain (the "Executive approver" badge, Workspace → People).
   * Held apart like `admins`, and for the same reason: it is a MONEY authority, set
   * by admins only, and deliberately NOT inherited from being an admin — an admin
   * configures the chain, it does not make them an approver. Optional for
   * back-compat (absent → nobody is an executive approver yet). 4 Oct 2026.
   */
  execApprovers?: string[];
}

/** Project role per person, one document each. */
export type Roles = Record<string, ProjectRole>;

/**
 * What each person may do on each board, by board id.
 *
 * Sparse on purpose: a person appears here only where a board has been given
 * an answer of its own, and everybody else falls back to `Roles` above. That
 * is what let per-board roles ship without migrating anybody.
 */
export type ProjectRoles = Record<string, Record<string, string>>;

/** Keyed by email, one document each — see the profiles rules for why. */
export type Profiles = Record<string, Profile>;

export const EMPTY_ACCESS: Access = { emails: [], admins: [] };

/** Everything before the @, tidied up — a decent name until someone sets a real one. */
export function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/**
 * What to show for a person. Falls back through the profile, then the email,
 * and finally returns whatever it was given — old comments stored a free-text
 * name rather than an address, and those must still read correctly.
 */
/**
 * A NAME IN TWO HALVES, AND THE ONE JOINED NAME EVERYTHING ELSE READS.
 *
 * The profile held a single `name`, which is the shape a small tool reaches
 * for and the shape every serious directory has moved away from: a first name
 * and a family name are separate facts, sorted, addressed and matched
 * separately, and no reliable rule recovers them from one string. "Van der
 * Berg" is one surname; "Mary Jane" is one given name.
 *
 * So both are stored, and `name` is kept as the joined form rather than
 * replaced. Everything that shows a person — comments, mentions, the trail —
 * reads `name` and none of it has to learn about halves. A profile written
 * before this existed still has only `name`, and still works.
 */
export const joinName = (first?: string, last?: string): string =>
  [first?.trim(), last?.trim()].filter(Boolean).join(' ');

/**
 * The two halves of a name somebody typed as one, for the first time they are
 * shown two boxes.
 *
 * A GUESS, AND ONLY EVER A STARTING POINT. Everything after the first space is
 * the surname, which is right for "Vinnie Narace" and wrong for "Mary Jane
 * Watson" — and there is no rule that is right for both, which is exactly why
 * two fields exist. It fills the boxes so nobody retypes their own name; what
 * they leave there is what is stored.
 */
export function splitName(name?: string): { first: string; last: string } {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

export function displayName(who: string, profiles: Profiles): string {
  if (!who) return 'Someone';
  const profile = profiles?.[who.toLowerCase()];
  // The halves win where they exist: they are what the person last typed, and
  // `name` is only ever the joined form of them.
  const joined = joinName(profile?.firstName, profile?.lastName);
  if (joined) return joined;
  if (profile?.name?.trim()) return profile.name.trim();
  // An assistant principal is "assistant:<handle>". Without a profile it must
  // still read as a name, never the raw token — "assistant:codey" leaking onto
  // the epic Owner cell beside real people is the R13 break (ledger L29/L75).
  // Humanise the handle: "codey" → "Codey", "deploy-bot" → "Deploy Bot". The
  // assistant's configured name lives in meta (see assistantName), which this
  // name-only helper has no access to; the handle is the best self-contained
  // fallback and is what every assistant principal carries.
  if (who.startsWith('assistant:')) {
    const handle = who.slice('assistant:'.length);
    const name = handle
      .split(/[._-]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return name || 'Assistant';
  }
  if (who.includes('@')) return nameFromEmail(who);
  return who;
}

/**
 * WHETHER A PERSON HAS A NAME, AS OPPOSED TO ONE THE SOFTWARE MADE UP.
 *
 * The People screen showed "no name set" beside "Maya Ferreira", which is the
 * screen contradicting itself in the width of one row. It tested `profile.name`
 * while the name being displayed came from `firstName`/`lastName` — the halves
 * the Add someone page writes, which `displayName` above rightly prefers.
 *
 * THE `name` FIELD ALONE CANNOT ANSWER THIS, which is why this is not a
 * one-liner. Everybody gets a `name`: it falls back to the part before the @ so
 * nobody appears to the workspace as a raw address while they wait to sign in.
 * So a set `name` may mean somebody typed it, or may mean nobody has. Compared
 * against what the fallback WOULD produce, the two come apart — and a name that
 * predates the halves, typed by a person, still counts.
 */
export function isNamed(who: string, profiles: Profiles): boolean {
  const profile = profiles?.[who.toLowerCase()];
  if (joinName(profile?.firstName, profile?.lastName)) return true;
  const typed = profile?.name?.trim();
  if (!typed) return false;
  return !who.includes('@') || typed !== nameFromEmail(who);
}

/**
 * IS THIS PERSON AN EXTERNAL VENDOR — the one test behind the Vendor name-tag
 * (Feature B, B-07). It is driven by WHO they are (their workspace company role),
 * not by what they can do on any one board: a supplier reads as a vendor everywhere
 * their name appears, even on a board where they happen to hold a fuller access role.
 * "Who they are" (this tag) and "what they can do here" (the board access role) are
 * related but not the same thing. Every list that shows a name tags from this one
 * resolver, so the tag can never appear in one place and be missing in another.
 */
export function isExternalVendor(who: string, profiles: Profiles): boolean {
  if (!who) return false;
  return profiles?.[who.toLowerCase()]?.companyRole === 'vendor';
}

export function initials(who: string, profiles: Profiles): string {
  return initialsFromName(displayName(who, profiles));
}

/**
 * Initials for a name that has ALREADY been resolved to what the screen shows,
 * so an avatar can never disagree with the name printed beside it. `initials`
 * above resolves the key through `displayName` first; a caller that supplies its
 * own fallback for an empty author — the card feed shows "FlightDeck" for the
 * app's own lines — must take the avatar from that same shown label, not from
 * re-resolving the empty key to "Someone" and landing on "SO".
 */
export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
}


/**
 * What the screen says once a sign-in link has been sent.
 *
 * A function, and tested, because it was written as text followed by an
 * expression on the next line — and JSX strips the newline between them, so it
 * rendered "The sender isnoreply@…". That is the one screen a person reads when
 * the email has not arrived, and the address is the thing they came for.
 */
export function linkSentHint(sender: string): string {
  return `It can take a minute and may land in spam. The sender is ${sender}.`;
}

/**
 * MANY PEOPLE AT ONCE, from whatever somebody has to hand.
 *
 * THE PROBLEM. Adding a development team one address at a time is twenty
 * presses and twenty chances to mistype. What people actually have is a list —
 * pasted out of an email, a spreadsheet column, a chat message — and every one
 * of those separates addresses differently. So this accepts all of them rather
 * than asking anybody to reformat.
 *
 * IT SORTS RATHER THAN REFUSES. A pasted list will contain something wrong —
 * a name, a stray heading, somebody already added — and rejecting the whole
 * paste for one bad line means finding it by eye and pasting again. Instead
 * every address is put in one of three piles and the screen shows all three,
 * so the good ones go in and the rest are named.
 *
 * "Firstname Lastname <name@example.com>" is handled because that is what
 * copying out of an email client gives you, and asking somebody to strip it by
 * hand is asking them to do the computer's job.
 */
export interface AddressList {
  /** Ready to add. Lower-cased, de-duplicated, in the order given. */
  ready: string[];
  /** Already in the workspace — not an error, and not worth doing twice. */
  already: string[];
  /** Not an address. Kept as typed, so it can be found in the original. */
  wrong: string[];
}

export function parseAddresses(text: string, existing: string[] = []): AddressList {
  const have = new Set(existing.map((e) => e.trim().toLowerCase()));
  const seen = new Set<string>();
  const out: AddressList = { ready: [], already: [], wrong: [] };

  const take = (value: string, asTyped: string) => {
    const e = value.replace(/^[<"']+|[>"',.;]+$/g, '').toLowerCase();
    if (!e.includes('@') || !e.includes('.') || /\s/.test(e)) {
      out.wrong.push(asTyped);
      return;
    }
    if (seen.has(e)) return;
    seen.add(e);
    (have.has(e) ? out.already : out.ready).push(e);
  };

  /*
   * A LINE AT A TIME, and the angle brackets win.
   *
   * Splitting the whole paste on whitespace first looks simpler and is wrong:
   * "Pat Moore <pat@x.com>" becomes three tokens, the address parses, and the
   * person's NAME is reported as two things that are not addresses. The screen
   * then says "3 not an address: Pat, Moore, Vinnie" when only Vinnie was a
   * mistake — noise that hides the one real problem in a list of forty.
   *
   * So: if a line names addresses in angle brackets, those are the addresses
   * and the rest of the line is somebody's name. Only a line with no brackets
   * is split on separators.
   */
  for (const line of text.split(/[\n\r]+/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const angled = [...trimmed.matchAll(/<([^>]+)>/g)].map((m) => m[1]);
    if (angled.length) {
      for (const a of angled) take(a, a);
      /*
       * AND THE REST OF THE LINE, which the first version dropped SILENTLY.
       * "Pat Moore <pat@x.com>; sam@x.com" is one line with one bracketed
       * address and one bare one, and taking only the bracketed one lost Sam
       * without a word — worse than the noise it was fixing, because noise is
       * visible and a missing person is not.
       *
       * On a line that names anybody in brackets, a word without an @ is part
       * of somebody's name and a word with one is an address. That rule keeps
       * both and reports neither wrongly.
       */
      for (const piece of trimmed.replace(/[^\s,;]*<[^>]+>/g, ' ').split(/[\s,;]+/))
        if (piece.includes('@')) take(piece.trim(), piece.trim());
      continue;
    }
    for (const piece of trimmed.split(/[\s,;]+/)) if (piece.trim()) take(piece.trim(), piece.trim());
  }
  return out;
}

/** What the screen says about a paste before anything is written. */
export function describeAddresses(list: AddressList): string {
  const parts: string[] = [];
  if (list.ready.length) parts.push(`${list.ready.length} to add`);
  if (list.already.length) parts.push(`${list.already.length} already here`);
  if (list.wrong.length) parts.push(`${list.wrong.length} not an address: ${list.wrong.slice(0, 3).join(', ')}${list.wrong.length > 3 ? '…' : ''}`);
  return parts.join(' · ');
}

/**
 * WHETHER THE BOARD IS HELD BACK UNTIL THIS PERSON CHOOSES A PASSWORD — #304.
 *
 * PULLED OUT OF App SO THAT SOMETHING CAN CHECK IT. It was one condition in the
 * middle of a component, which meant the only way to try it was to be an
 * account in each of these states and sign in. Vinnie, walking #304 on 1
 * September 2026: *"I cannot create an account that has not set a password, so
 * I cannot walk this."* Quite — the app only ever hands out accounts WITH a
 * password now, so the half of this that must keep working is the half nobody
 * can reach by hand. A rule that can only be tested by arranging a state the
 * product no longer produces is a rule that stops being tested.
 *
 * The four facts, and why each one is here rather than assumed:
 *
 *   staff        Somebody looking in from the operator portal is not a person
 *                on this board and has no password to set here.
 *   profilesOk   Absent is not "no", and a FAILED read is not "no" either. The
 *                profiles arriving late is what showed this screen to people who
 *                had long since chosen one (#252); a profiles read that ERRORED
 *                does the same, because the empty map it leaves reads exactly
 *                like "never set one". So the gate speaks only on a read that
 *                SUCCEEDED — not merely one that answered. A failed or pending
 *                read falls through to the board the browser kept, not to this.
 *   assistant    It has no inbox, there was never a link, and the password it
 *                was handed IS its password. Nothing to protect, everything to
 *                break: the account it locked out was our own.
 *   passwordSetAt Never means never. This is the case the gate exists for.
 */
export function mustChoosePassword(
  profile: Profile | undefined,
  { staff, profilesOk }: { staff: boolean; profilesOk: boolean }
): boolean {
  if (staff) return false;
  if (!profilesOk) return false;
  if (profile?.assistant) return false;
  return !profile?.passwordSetAt;
}

/**
 * WHETHER THIS PERSON IS DEACTIVATED, AND SO HELD OUT OF THE BOARD — 0208.
 *
 * The sibling of `mustChoosePassword`, decided in the same place and with the same
 * four guards, for the same reason: it is one condition the booted session applies
 * to the people row, and keeping it beside the password gate means both are read
 * from the row the app already has rather than re-derived somewhere a script skips.
 *
 *   staff        Somebody from the operator portal is not a person on this board;
 *                they have no membership here to suspend.
 *   profilesOk   Absent is not "suspended", and a FAILED read is not either — the
 *                empty map a failed read leaves must never read as everyone paused.
 *                So the gate speaks only on a read that SUCCEEDED.
 *   assistant    It never signs in through a screen (push-tracker goes straight to
 *                Supabase Auth), so a gate here would only lock out our own writer
 *                for a human-offboarding reason that does not apply to it.
 *   suspendedAt  Set means paused; the person keeps every role and board, and
 *                clearing it (reactivate) lets them straight back in.
 */
export function amSuspended(
  profile: Profile | undefined,
  { staff, profilesOk }: { staff: boolean; profilesOk: boolean }
): boolean {
  if (staff) return false;
  if (!profilesOk) return false;
  if (profile?.assistant) return false;
  return !!profile?.suspendedAt;
}
